import { Button, Table, Tooltip } from "antd";
import ChantPopConfirm from "../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { invalidateStadiumCache, postUpdateCDNUtil } from "../../../helperFunctions/util";
import {
  fbDeletePartner,
  fbGetPartners,
  fbGetDonationDataForGroup,
  setPartnerSortOrder,
} from "../../../services/firebaseService/endPoints/admin/partners";
import {
  GUIDE_SETUP,
  TOPIC_ADD_PARTNERS,
} from "../../../constants/gettingStarted";
import { useNavigate } from "react-router-dom";
import { DescriptionText } from "../../../CommonComponents/index.js";
import { toast } from "react-toastify";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import useCurrency from "../../../utils/hooks/useCurrency.js";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import {
  HolderOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { debounce } from "../../../utils/helper";
import logger from "../../../utils/logger";

const RowContext = React.createContext({});

const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  const childrenArray = React.Children.toArray(props.children);
  if (childrenArray.length > 0) {
    childrenArray[0] = React.cloneElement(childrenArray[0], {});
  }

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes}>
        {childrenArray}
      </tr>
    </RowContext.Provider>
  );
};

const PartnersList = () => {
  const [tableData, setTableData] = useState([]);

  const groupData = useAdminDataStore(selectCurrentGroupData);
  const groupCurrency = useCurrency();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const updatePartnersSortOrder = useCallback(
    async (updatedTableData, originalTableData) => {
      try {
        // Only update partners whose sortIndex changed
        const changedPartners = {};
        updatedTableData.forEach((partner, index) => {
          const newSortIndex = index + 1;
          const originalPartner = originalTableData.find(
            (p) => p.id === partner.id
          );
          const oldSortIndex = originalPartner?.sortIndex;

          // Update if sortIndex changed or doesn't exist
          if (oldSortIndex !== newSortIndex) {
            changedPartners[partner.id] = newSortIndex;
          }
        });

        // Only make the API call if there are changes
        if (Object.keys(changedPartners).length > 0) {
          await setPartnerSortOrder(changedPartners, groupData?.id);
          postUpdateCDNUtil(groupData?.id);
          invalidateStadiumCache(groupData?.groupKey);
        }
      } catch (error) {
        logger.error("Error updating partner sort order:", error);
        toast.error("Error updating order");
      }
    },
    [groupData?.id]
  );

  const debouncedUpdatePartnersSortOrder = useMemo(
    () =>
      debounce(
        (updatedData, originalData) =>
          updatePartnersSortOrder(updatedData, originalData),
        3000
      ),
    [updatePartnersSortOrder]
  );

  const DragHandle = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        style={{ cursor: "move", width: "20px", padding: 0 }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const fetchPartnersList = async () => {
    try {
      setLoading(true);
      const partners = await fbGetPartners(groupData?.id);
      const donations = await fbGetDonationDataForGroup(groupData?.id);
      const currentYear = new Date().getFullYear();
      const previousYear = currentYear - 1;
      const twoYearsAgo = currentYear - 2;
      const startOfCurrentYear = new Date(currentYear, 0, 1).getTime();

      const donationTotals = donations.reduce((acc, donation) => {
        const id = donation?.partnerId;
        const amount = Number(donation?.amount) || 0;
        if (!id || amount <= 0) return acc;

        const donationDate = donation?.date?.toDate
          ? donation.date.toDate()
          : new Date(donation?.date);
        const donationTime =
          donationDate instanceof Date ? donationDate.getTime() : NaN;
        const donationYear =
          donationDate instanceof Date ? donationDate.getFullYear() : undefined;
        const isCurrentYear =
          !Number.isNaN(donationTime) && donationTime >= startOfCurrentYear;

        const existing = acc[id] || { total: 0, years: {} };
        existing.total += amount;
        if (isCurrentYear && donationYear === currentYear) {
          existing.years[currentYear] =
            (existing.years[currentYear] || 0) + amount;
        } else if (
          donationYear === previousYear ||
          donationYear === twoYearsAgo
        ) {
          existing.years[donationYear] =
            (existing.years[donationYear] || 0) + amount;
        }
        acc[id] = existing;
        return acc;
      }, {});

      if (partners?.length > 0) {
        useAdminDataStore.getState().setGettingStartedItemStatus(
          groupData?.id,
          GUIDE_SETUP,
          TOPIC_ADD_PARTNERS,
          true
        );
      }

      // Sort partners by sortIndex (handle missing sortIndex)
      const sortedPartners = partners.sort((row1, row2) => {
        const index1 = row1.sortIndex ?? Number.MAX_SAFE_INTEGER;
        const index2 = row2.sortIndex ?? Number.MAX_SAFE_INTEGER;
        return index1 - index2;
      });

      const partnersWithDonations = sortedPartners.map((partner) => ({
        ...partner,
        totalDonations:
          donationTotals[partner.id]?.total ?? partner.totalDonations ?? 0,
        currentYearDonations:
          donationTotals[partner.id]?.years?.[currentYear] ??
          partner.currentYearDonations ??
          0,
        yearlyDonations: donationTotals[partner.id]?.years || {},
      }));

      setTableData(partnersWithDonations);
    } catch (error) {
      setTableData([]);
      toast.error("Failed to fetch partners list");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPartner = (data) => {
    navigate(`/admin/partners/edit/${data.id}`);
  };

  const handleDeletePartner = (id) => {
    fbDeletePartner(groupData?.id, id).then(() => {
      postUpdateCDNUtil(groupData?.id);
      invalidateStadiumCache(groupData?.groupKey);
      fetchPartnersList();
    });
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setTableData((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );

        const updatedSortOrder = arrayMove(prevState, activeIndex, overIndex);

        // Pass both updated and original data for comparison
        debouncedUpdatePartnersSortOrder(updatedSortOrder, prevState);

        return updatedSortOrder;
      });
    }
  };

  useEffect(() => {
    if (groupData?.id) {
      fetchPartnersList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const columns = [
    {
      key: "sort",
      align: "center",
      width: 20,
      className: "header-white",
      render: () => {
        return (
          <div>
            <DragHandle />
          </div>
        );
      },
    },
    {
      title: "Action",
      width: 100,
      className: "header-white",
      render: (data) => {
        return (
          <div className="flex-center" style={{ gap: "4px" }}>
            <Tooltip title="edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ fontSize: 18 }} />}
                onClick={() => handleEditPartner(data)}
              />
            </Tooltip>
            <ChantPopConfirm
              onCancel={() => {}}
              onConfirm={() => handleDeletePartner(data.id)}
              title="Are You Sure? Do not delete if donations have been collected. To hide parter, unselect all 'Display On' options in the form"
            >
              <Tooltip title="delete">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined style={{ fontSize: 18 }} />}
                />
              </Tooltip>
            </ChantPopConfirm>
          </div>
        );
      },
    },
    {
      title: "Partner",
      className: "header-white",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Active?",
      className: "header-white",
      key: "active",
      dataIndex: "status",
    },
    {
      title: "Displayed",
      className: "header-white",
      key: "displayed",
      dataIndex: "displayOn",
      render: (data) => data && data.join(", "),
    },
    {
      title: `Total donations(${groupCurrency})`,
      className: "header-white",
      key: "totalDonations",
      dataIndex: "totalDonations",
      render: (data) =>
        data && `${groupCurrency}${parseFloat(`${data}`).toFixed(2)}`,
    },
  ];

  return (
    <div className="col">
      <DescriptionText showBackground>
        <p>
          Add community and commercial partners. Collect donations (requires
          Stripe account from Chant). Set to appear across the app, website and
          stadium web pages. Consider adding your group as a partner to collect
          donations for tifo, operating expenses, etc.
        </p>
        <p>
          Partners with a discount and/or QR code uploaded will also appear
          under the Member Card as an easy way to claim the discount.
        </p>
      </DescriptionText>
      <div
        className="col"
        style={{
          marginTop: "var(--screen-general-margin)",
        }}
      >
        <div style={{ marginTop: "var(--screen-general-margin)" }}>
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={tableData.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                bordered
                columns={columns}
                components={{
                  body: {
                    row: Row,
                  },
                }}
                loading={loading}
                dataSource={tableData}
                rowKey="id"
                className="table-backdrop"
                bodyStyle={{ backgroundColor: "#ffffff" }}
                pagination={{
                  position: ["bottomLeft"],
                  defaultPageSize: 100,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                }}
              />
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
};

export default PartnersList;
